import type { WorkChapter } from './schema'
import { method } from './method'
import { tools } from './tools'
import { automations, cover, discProfile, liveSites, workCount } from './work'

/**
 * Home page copy. The hero pairs a thin line with a bold one. The bold line is sized in `cqi` in
 * features/home/hero/hero.css so it fills the column: re-measure it when you change the words
 * (see the comment on `.hero__title` there).
 */
export const homeHero = {
  headlineThin: 'GoHighLevel,',
  headlineBold: 'built to run itself.',
  subhead: 'GoHighLevel virtual assistant and automation specialist. 10+ years of experience, 50+ automations, based in the Philippines (GMT+8).',
  cta: { label: 'Get in touch', to: '/#contact' },
}

/**
 * The statement under the hero: words light up as the page scrolls, each keyword lands bold and
 * moves the tracker. Keep four keywords (the tracker shows them in order).
 * A string is plain words; `{ key }` is a keyword.
 */
export type ManifestoPart = string | { key: string }

export const homeManifesto = {
  eyebrow: method.eyebrow,
  parts: [
    'I set up',
    { key: 'CRMs,' },
    { key: 'workflows,' },
    { key: 'funnels' },
    'and',
    { key: 'Voice AI' },
    'in HighLevel for coaches, agencies and e-commerce brands, then keep them running.',
  ] satisfies ManifestoPart[],
}

export const homeSections = {
  work: {
    eyebrow: 'Selected work',
    title: 'Builds you can open right now.',
  },
}

/* ---------- Work section ---------- */

/** The URL of a chapter's showcase sheet. Real, shareable and in the sitemap. */
export const chapterPath = (id: string) => `/work/${id}`

export const workSection = {
  ...homeSections.work,
  /** The title as one display line in the hero's thin + bold pairing. */
  titleThin: 'Builds you can',
  titleBold: 'open right now.',
  all: { label: `See all ${workCount} builds`, to: '/work' },
}

/**
 * The Work chapters, in bento order. Add, remove or reorder a chapter here; the bento, the sheets and
 * the sitemap follow (the bento's grid areas in work.css are keyed by chapter id). A chapter with a
 * `lead` is a single build; one with no landscape picture shows its `marks` as a logo face instead.
 */
export const workChapters: WorkChapter[] = [
  {
    id: 'websites',
    title: 'Websites',
    line: 'Live sites and funnels built in GoHighLevel.',
    count: liveSites.length,
    unit: 'sites',
    description: 'Live sites built in GoHighLevel, from a landing page and an offer funnel to an agency website. Each one opens the live site.',
    marks: [tools.funnels, tools.gohighlevel, tools.calendars],
    items: liveSites.map((s) => ({
      id: s.id,
      name: s.label,
      kicker: 'Website',
      summary: s.summary,
      image: cover(s.id, `${s.label} cover card`, 1280, 720),
      href: s.url,
    })),
  },
  {
    id: 'automations',
    title: 'AI Automations',
    line: 'Workflows from client accounts, in the HighLevel builder.',
    count: automations.length,
    unit: 'systems',
    description: 'AI automations built inside GoHighLevel for client accounts: AI nurture, WhatsApp IVR routing and Voice AI agents.',
    marks: [tools.workflows, tools.gohighlevel, tools.voiceAi],
    items: automations.map((a) => ({
      id: a.id,
      name: a.name,
      kicker: a.kicker,
      summary: a.summary,
      stack: a.stack,
      status: a.status,
      image: a.image,
      href: a.href,
    })),
  },
  {
    id: 'disc-profile',
    title: 'DISC Profile',
    line: 'How I work, measured.',
    count: 1,
    unit: 'profile',
    description: 'Certified Tony Robbins DISC and Motivators assessment (ASI, 2026): S/C/D, The Planner, with the full scores and motivators.',
    marks: [tools.googleWorkspace],
    items: [],
    lead: cover('disc-profile', 'DISC and Motivators profile: S/C/D, The Planner, with the DISC scores', 1440, 900),
    link: discProfile.link,
  },
]

export const findChapter = (id: string | undefined) => workChapters.find((chapter) => chapter.id === id)
